import { z } from "zod"

import type { ReportCreationMedia } from "@/components/vivid-report-editor/report-creation-media"
import type { FormResponse, Responses } from "@/models/new/formResponse.types"
import type { Form } from "@/models/new/forms.types"

import { endpoints } from "./endpoints"

const appUrl = z.string().refine((value) => {
  try {
    return new URL(value).protocol === "vivid-mobile:"
  } catch {
    return false
  }
}, "Invalid Vivid app URL")

const appOnlySchema = z.object({ mode: z.literal("app-only"), appUrl }).strict()
const publicSchema = z
  .object({
    mode: z.literal("public"),
    appUrl,
    form: z
      .object({ id: z.string().min(1), title: z.string(), fields: z.array(z.unknown()) })
      .passthrough(),
    site: z.object({ id: z.string().min(1), name: z.string().min(1) }).strict(),
    listItemsMap: z.record(
      z.array(z.object({ id: z.string(), code: z.string(), value: z.string() }).strict()),
    ),
    formRevision: z.string().min(1),
  })
  .strict()
const entrySchema = z.discriminatedUnion("mode", [appOnlySchema, publicSchema])
const receiptSchema = z
  .object({ submitted: z.literal(true), receiptId: z.string().min(1) })
  .strict()

export interface PublicIssueData {
  mode: "public"
  appUrl: string
  form: Form
  site: { id: string; name: string }
  listItemsMap: Record<string, Array<{ id: string; code: string; value: string }>>
  formRevision: string
}

export interface AppOnlyIssueData {
  mode: "app-only"
  appUrl: string
}

export type PublicIssueEntry = PublicIssueData | AppOnlyIssueData

export interface PublicIssuePayload {
  submissionId: string
  formRevision: string
  contact?: { name?: string; email?: string; phone?: string }
  responses: Responses
  fieldMedia?: FormResponse["fieldMedia"]
  website: string
}

type UploadDescriptor =
  | {
      uploadId: string
      kind: "response"
      fieldId: string
      filename: string
      mimeType: string
      size: number
      notes?: string
    }
  | {
      uploadId: string
      kind: "fieldMedia"
      locator: string
      target: ReportCreationMedia["fieldMedia"][string]["target"]
      filename: string
      mimeType: string
      size: number
    }

const url = (params: { organizationId: string; formId: string; linkId: string }) =>
  endpoints.public.issue
    .replace(":organizationId", encodeURIComponent(params.organizationId))
    .replace(":formId", encodeURIComponent(params.formId))
    .replace(":linkId", encodeURIComponent(params.linkId))

const parseError = async (response: Response) => {
  const data = await response.json().catch(() => undefined)
  throw Object.assign(new Error(`HTTP ${response.status}`), { status: response.status, data })
}

export const getPublicIssue = async (
  params: { organizationId: string; formId: string; linkId: string },
  signal?: AbortSignal,
): Promise<PublicIssueEntry> => {
  const response = await fetch(url(params), { signal, credentials: "omit" })
  if (!response.ok) return parseError(response)
  return entrySchema.parse(await response.json()) as PublicIssueEntry
}

export const buildPublicIssueSubmission = (
  payload: PublicIssuePayload,
  media: ReportCreationMedia,
): { body: BodyInit; multipart: boolean; descriptors: UploadDescriptor[] } => {
  const fieldMedia = Object.fromEntries(
    Object.entries(media.fieldMedia).map(([locator, bundle]) => [
      locator,
      { target: bundle.target, ...(bundle.note ? { note: bundle.note } : {}), attachments: [] },
    ]),
  )
  const finalPayload = {
    ...payload,
    ...(Object.keys(fieldMedia).length ? { fieldMedia } : {}),
  }
  const descriptors: UploadDescriptor[] = []
  const files = new Map<string, File>()
  for (const [responseKey, uploads] of Object.entries(media.responseUploads)) {
    for (const upload of uploads) {
      descriptors.push({
        uploadId: upload.uploadId,
        kind: "response",
        fieldId: responseKey,
        filename: upload.file.name,
        mimeType: upload.file.type || "application/octet-stream",
        size: upload.file.size,
        ...(upload.notes ? { notes: upload.notes } : {}),
      })
      files.set(upload.uploadId, upload.file)
    }
  }
  for (const [locator, bundle] of Object.entries(media.fieldMedia)) {
    for (const upload of bundle.uploads) {
      descriptors.push({
        uploadId: upload.uploadId,
        kind: "fieldMedia",
        locator,
        target: bundle.target,
        filename: upload.file.name,
        mimeType: upload.file.type || "application/octet-stream",
        size: upload.file.size,
      })
      files.set(upload.uploadId, upload.file)
    }
  }
  if (!descriptors.length)
    return { body: JSON.stringify(finalPayload), multipart: false, descriptors }
  const body = new FormData()
  body.append("manifest", JSON.stringify({ payload: finalPayload, uploads: descriptors }))
  descriptors.forEach((descriptor) =>
    body.append(descriptor.uploadId, files.get(descriptor.uploadId)!, descriptor.filename),
  )
  return { body, multipart: true, descriptors }
}

export const submitPublicIssue = async (
  params: { organizationId: string; formId: string; linkId: string },
  submission: ReturnType<typeof buildPublicIssueSubmission>,
): Promise<{ submitted: true; receiptId: string }> => {
  const response = await fetch(url(params), {
    method: "POST",
    credentials: "omit",
    headers: submission.multipart ? undefined : { "Content-Type": "application/json" },
    body: submission.body,
  })
  if (!response.ok) return parseError(response)
  return receiptSchema.parse(await response.json())
}
